import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Sparkles, ArrowRight, CalendarDays, Users, Star, LayoutTemplate,
  ClipboardCheck, MessageSquareText, Shield, Theater, Music4, Building2,
  FileSignature, Wallet, Video, Layers, BarChart3, ScrollText,
  PhoneCall, Mail, Zap,
} from "lucide-react";

const STAGE_IMG = "https://images.unsplash.com/photo-1613210434051-4b00d62d03fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxlbXB0eSUyMHRoZWF0ZXIlMjBzdGFnZSUyMGRhcmslMjBzcG90bGlnaHR8ZW58MHx8fHwxNzgzMzU0OTI3fDA&ixlib=rb-4.1.0&q=85";
const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

const modules = [
  { icon: Star, tint: "#F59E0B", title: "Auditions & Casting",
    body: "Public signup links, unlimited custom fields, virtual submissions with inline video, callback rounds, slot scheduling, and a secure judge portal that hides contact info." },
  { icon: CalendarDays, tint: "#EF4444", title: "Rehearsal Scheduling",
    body: "Drag-and-drop calendar, per-cast call times, overlap warnings, and printable call sheets — everything the SM already juggles on paper, now shared with the whole company." },
  { icon: ClipboardCheck, tint: "#3B82F6", title: "Attendance & Conflicts",
    body: "One-tap sign-in on any phone, side-by-side conflict review, exportable CSVs, and reminders so nobody 'forgets' they had a doctor's appointment." },
  { icon: MessageSquareText, tint: "#10B981", title: "Cast Notes & Reports",
    body: "Nightly rehearsal reports with reusable templates, priority-tagged cast notes delivered by real transactional email, and actionables that auto-follow-up." },
  { icon: FileSignature, tint: "#A855F7", title: "Forms & Compliance",
    body: "No-code builder for contracts, media releases, and policy acknowledgements. Required forms hard-block the app until signed. Draw or type digital signatures. PDF export. Company-wide compliance dashboard." },
  { icon: Building2, tint: "#EC4899", title: "Facilities & Rooms",
    body: "Multi-facility room scheduling with double-book prevention and a cross-facility week calendar colour-coded by show — overlaps surface at a glance." },
  { icon: Layers, tint: "#14B8A6", title: "Season Planner",
    body: "Set up every show in your season from one canvas: cast lists, staff roles, opening dates, colour lanes. Onboarding scales from one production to a full year." },
  { icon: Wallet, tint: "#EAB308", title: "Budget Tracking",
    body: "Per-show budget with category rollups, receipt attachments, and role-scoped visibility so producers see totals while designers see only their line." },
  { icon: ScrollText, tint: "#F97316", title: "Script Intelligence",
    body: "Upload the script once. StageCall extracts scenes, songs, and characters — powering scene breakdowns, actor lookups, and printable running orders." },
  { icon: Video, tint: "#06B6D4", title: "Virtual Auditions",
    body: "Auditioners submit YouTube, Vimeo, or Drive links; judges watch inline and drop timestamped notes without ever leaving the portal." },
  { icon: Shield, tint: "#8B5CF6", title: "Multi-Tenant Security",
    body: "Six-tier role model (super admin, org admin, producer, staff, support, cast). Strict tenant isolation. Signed URLs on every file. Judge portal never leaks contact info." },
  { icon: BarChart3, tint: "#22C55E", title: "Reports & Analytics",
    body: "Compliance completion, attendance heatmaps, budget burn-down, audition pipeline — every number your board wants to see, one click away." },
];

const personas = [
  { role: "Producers", body: "See the whole season on one canvas. Track budgets, compliance, and casting progress across every show without asking anyone for a status update." },
  { role: "Stage Managers", body: "Publish a nightly rehearsal report in under two minutes, batch cast notes by priority, and never chase attendance again." },
  { role: "Directors", body: "Score every audition on your phone, watch virtual submissions inline, and cast without pushing spreadsheets around." },
  { role: "Cast & Crew", body: "Your call times, your notes, your paperwork — all in one place, on your phone, with real email reminders." },
];

const bulletStats = [
  { k: "12", v: "integrated modules — no add-ons to buy" },
  { k: "1-click", v: "print-to-PDF for call sheets, reports, signed forms" },
  { k: "0", v: "email addresses ever shared with judges" },
  { k: "Real", v: "transactional email + digital signatures" },
];



export default function Landing() {
  const { user } = useAuth();
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoKind, setDemoKind] = useState("demo");
  const [form, setForm] = useState({ name: "", email: "", org: "", message: "" });
  const [sending, setSending] = useState(false);

  if (user && user.role) {
    return <Navigate to={user.role === "super_admin" ? "/platform/overview" : "/productions"} replace />;
  }

  const openLead = (kind) => { setDemoKind(kind); setDemoOpen(true); };
  const submitLead = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim()) {
      toast.error("Please share your name and email so we can follow up.");
      return;
    }
    setSending(true);
    try {
      await api.post("/public/leads", { ...form, kind: demoKind });
      toast.success("Got it — someone from the team will be in touch within one business day.");
      setDemoOpen(false);
      setForm({ name: "", email: "", org: "", message: "" });
    } catch {
      // Fallback: mailto so the message never gets lost even if the endpoint isn't wired yet.
      const subject = encodeURIComponent(`StageCall ${demoKind === "sales" ? "sales inquiry" : "demo request"} — ${form.org || form.name}`);
      const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\nOrg: ${form.org}\n\n${form.message}`);
      window.location.href = `mailto:hello@stagecall.app?subject=${subject}&body=${body}`;
      setDemoOpen(false);
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Sticky nav */}
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0A0A0A]/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md grid place-items-center bg-gold/15 text-gold"><Sparkles className="w-4 h-4" /></div>
            <div>
              <div className="font-display text-lg leading-none">StageCall</div>
              <div className="text-[9px] uppercase tracking-[0.24em] text-zinc-500 mt-0.5">Production Desk</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm text-zinc-400">
            <a href="#modules" className="hover:text-white">Modules</a>
            <a href="#who" className="hover:text-white">Who it&apos;s for</a>
            <a href="#contact" className="hover:text-white">Contact</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="h-9 px-3 rounded-md border border-white/15 text-zinc-200 hover:bg-white/5 text-sm inline-flex items-center gap-1.5" data-testid="nav-signin">
              Sign in
            </Link>
            <button
              onClick={() => openLead("demo")}
              data-testid="nav-book-demo"
              style={goldBtn}
              className="h-9 px-3 rounded-md font-semibold text-sm hover:brightness-110 hidden sm:inline-flex items-center gap-1.5"
            ><PhoneCall className="w-3.5 h-3.5" /> Book a demo</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url(${STAGE_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/40 via-[#0A0A0A]/85 to-[#0A0A0A]" />
        <div className="absolute inset-0 grain-overlay opacity-30" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-16 md:pt-28 pb-20 md:pb-32">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold border border-gold/25 rounded-full px-3 py-1">
            <Sparkles className="w-3 h-3" /> The all-in-one production desk
          </div>
          <h1 className="mt-6 font-display text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            Every rehearsal, audition, and paperwork <span className="text-gold">on one call sheet.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-zinc-300 max-w-2xl">
            StageCall replaces the group text, the shared Drive, the paper attendance sheet,
            and the six spreadsheets nobody wants to open. Built with — and for — working theatre companies.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={() => openLead("demo")}
              data-testid="hero-book-demo"
              style={goldBtn}
              className="h-12 px-6 rounded-md font-semibold text-sm md:text-base inline-flex items-center gap-2 hover:brightness-110 shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_16px_36px_-12px_rgba(245,158,11,0.55)]"
            ><PhoneCall className="w-4 h-4" /> Book a 20-min demo</button>
            <Link to="/login" data-testid="hero-signin" className="h-12 px-5 rounded-md border border-white/20 hover:bg-white/5 text-sm md:text-base inline-flex items-center gap-2">
              Sign in <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {bulletStats.map((s) => (
              <div key={s.v} className="rounded-lg border border-white/10 bg-black/40 backdrop-blur p-4">
                <div className="font-display text-2xl text-gold">{s.k}</div>
                <div className="text-[11px] text-zinc-400 mt-1 leading-snug">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules grid */}
      <section id="modules" className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Everything you need. Nothing you don&apos;t.</div>
        <h2 className="mt-3 font-display text-3xl md:text-5xl tracking-tight max-w-3xl">
          Twelve integrated modules. One production desk.
        </h2>
        <p className="mt-4 text-zinc-400 max-w-2xl">
          No plug-in economy. No &ldquo;buy the calendar add-on to get attendance.&rdquo; Every module is on by default,
          scoped by role, and designed to work together the way a real theatre company operates.
        </p>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.title} className="group rounded-xl border border-white/10 bg-[#111] p-5 hover:border-white/20 hover:-translate-y-0.5 transition">
                <div className="w-10 h-10 rounded-md grid place-items-center" style={{ backgroundColor: `${m.tint}1a`, color: m.tint }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="mt-3 font-display text-lg">{m.title}</div>
                <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">{m.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Personas */}
      <section id="who" className="border-y border-white/5 bg-gradient-to-b from-black/40 to-transparent">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Built for the whole room</div>
          <h2 className="mt-3 font-display text-3xl md:text-5xl tracking-tight max-w-3xl">A view for every seat in the house.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {personas.map((p) => (
              <div key={p.role} className="rounded-xl border border-white/10 bg-[#0F0F0F] p-6">
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">For</div>
                <div className="mt-1 font-display text-2xl text-gold">{p.role}</div>
                <p className="mt-3 text-zinc-300 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing — hidden during pilot phase. Restore this block when we're
          ready to charge; the `tiers` array above still holds the plan data. */}

      {/* Trust strip */}
      <section className="border-y border-white/5 bg-[#0B0B0B]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3"><Zap className="w-5 h-5 text-gold shrink-0 mt-0.5" /><div><div className="text-sm font-semibold">Ships every week</div><div className="text-xs text-zinc-500 mt-0.5">Fast iteration cadence — see the changelog inside the app.</div></div></div>
          <div className="flex items-start gap-3"><Shield className="w-5 h-5 text-gold shrink-0 mt-0.5" /><div><div className="text-sm font-semibold">Compliance-ready</div><div className="text-xs text-zinc-500 mt-0.5">Digital signatures + audit-trail submissions with PDF export.</div></div></div>
          <div className="flex items-start gap-3"><LayoutTemplate className="w-5 h-5 text-gold shrink-0 mt-0.5" /><div><div className="text-sm font-semibold">Templates for everything</div><div className="text-xs text-zinc-500 mt-0.5">Reusable rehearsal reports and reusable form scaffolds — build once, run every season.</div></div></div>
        </div>
      </section>

      {/* Contact / CTA */}
      <section id="contact" className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="rounded-2xl border border-gold/25 bg-gradient-to-br from-gold/10 via-transparent to-transparent p-8 md:p-14 relative overflow-hidden">
          <div className="absolute inset-0 grain-overlay opacity-20" />
          <div className="relative max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Ready to see it?</div>
            <h2 className="mt-3 font-display text-3xl md:text-5xl tracking-tight">Bring your season. We&apos;ll show you the desk.</h2>
            <p className="mt-4 text-zinc-300">
              20 minutes, screen-shared, with real theatre data. No sales deck. Bring your stage manager
              and we&apos;ll build one production together, live.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => openLead("demo")} data-testid="footer-book-demo" style={goldBtn} className="h-12 px-6 rounded-md font-semibold text-sm inline-flex items-center gap-2 hover:brightness-110">
                <PhoneCall className="w-4 h-4" /> Book a demo
              </button>
              <button onClick={() => openLead("sales")} data-testid="footer-contact-sales" className="h-12 px-6 rounded-md border border-white/20 hover:bg-white/5 text-sm inline-flex items-center gap-2">
                <Mail className="w-4 h-4" /> Talk to sales
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-gold" /> <span>StageCall · Production Desk</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="mailto:hello@stagecall.app" className="hover:text-white">hello@stagecall.app</a>
            <Link to="/login" className="hover:text-white">Sign in</Link>
          </div>
        </div>
      </footer>

      {/* Lead-capture modal */}
      {demoOpen && (
        <div
          data-testid="lead-modal"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setDemoOpen(false); }}
        >
          <div className="w-full max-w-lg bg-[#0F0F0F] border border-white/10 rounded-xl shadow-2xl p-6 md:p-8">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">
                  {demoKind === "sales" ? "Talk to sales" : "Book a demo"}
                </div>
                <div className="mt-1 font-display text-2xl">Let&apos;s find 20 minutes.</div>
              </div>
              <button onClick={() => setDemoOpen(false)} className="text-zinc-500 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
            </div>
            <form onSubmit={submitLead} className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Your name</label>
                <input required data-testid="lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full h-10 rounded-md bg-black/50 border border-white/10 focus:border-gold px-3 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Work email</label>
                <input required type="email" data-testid="lead-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full h-10 rounded-md bg-black/50 border border-white/10 focus:border-gold px-3 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">Theatre / company</label>
                <input data-testid="lead-org" value={form.org} onChange={(e) => setForm({ ...form, org: e.target.value })} className="mt-1 w-full h-10 rounded-md bg-black/50 border border-white/10 focus:border-gold px-3 text-sm outline-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500">What are you hoping to solve?</label>
                <textarea rows={3} data-testid="lead-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full rounded-md bg-black/50 border border-white/10 focus:border-gold px-3 py-2 text-sm outline-none resize-none" />
              </div>
              <button data-testid="lead-submit" disabled={sending} type="submit" style={goldBtn} className="w-full h-11 rounded-md font-semibold text-sm inline-flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-60">
                {sending ? "Sending…" : (<>Send it <ArrowRight className="w-4 h-4" /></>)}
              </button>
              <div className="text-[11px] text-zinc-500 text-center">We reply within one business day. No wait-list, no dark-pattern demo trap.</div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
